import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Button,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowUp,
  Plus,
  Folder,
  X,
  ArrowLeft,
  ChevronDown,
  User,
  Bot,
  Microscope,
  LibraryBig
} from 'lucide-react';
import agtEduService from '../services/agtEduService';
// Import FileUploader component
import FileUploader from '../components/FileUploader';
// Import IndividualFileUploader component
import IndividualFileUploader from '../components/IndividualFileUploader';
// Import MagicalMessageRenderer component
import MagicalMessageRenderer from '../components/MagicalMessageRenderer';
// Import ThinkingIcon component
import ThinkingIcon from '../components/ThinkingIcon';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

const SubjectSelectorMenu = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: isMobile ? 'calc(100vw - 48px)' : '16rem',
    marginTop: theme.spacing(1),
    backgroundColor: colors.background,
    border: `1px solid ${colors.borderColor}`,
    borderRadius: theme.shape.borderRadius * 2,
    zIndex: 10
  };
});

const MessageContainer = styled(Box)(({ theme, role }) => {
  
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: role === 'user' ? 'flex-end' : 'flex-start',
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: role === 'user' ? '70%' : '100%', // Usuario 70%, asistente 100%
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  };
});

const MessageBubble = styled(Box)(({ theme, role }) => {
  const colors = navigationColors(theme);
  
  return {
    maxWidth: role === 'user' ? '80%' : '100%',
    width: role === 'assistant' ? '100%' : 'auto',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(4),
    backgroundColor: role === 'user' 
    ? theme.palette.mode === 'light' ? '#E2E2E7' : colors.cardBackground // Color más oscuro para modo claro
    : 'transparent', // Transparente para el asistente
    color: colors.textDefault,
    position: 'relative',
    '& p': {
      margin: 0,
      lineHeight: 1.5
    }
  };
});

const ScrollableMessagesContainer = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  
  return {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    maxHeight: '60vh',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: colors.backgroundTertiary,
    borderRadius: theme.spacing(2)
  };
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const ChatInputMUI = ({ onConversationUpdate, chatMode = false, currentConversation = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const messagesEndRef = useRef(null);
  
  // Tiempo mínimo para mostrar el indicador thinking
  const MIN_THINKING_DISPLAY_TIME = 1500; // ms - ensure this matches the value in DocumentEditorView
  
  // Pagination state
  const [filesCurrentPage, setFilesCurrentPage] = useState(0);
  const [filesPerPage] = useState(5);
  const [subjectsPerPage] = useState(6);
  
  // UI State
  const [message, setMessage] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [showSubjectInput, setShowSubjectInput] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showSubjectSelector, setShowSubjectSelector] = useState(false);
  const [selectedChatSubject, setSelectedChatSubject] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false); // Estado añadido para tracking de carga de mensajes
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState({ show: false, type: '', message: '' });
  const [showIndividualFilesDialog, setShowIndividualFilesDialog] = useState(false);
  
  // States for individual files
  const [individualFiles, setIndividualFiles] = useState([]);
  const [selectedIndividualFiles, setSelectedIndividualFiles] = useState([]);
  
  // State to control thinking indicator visibility
  const [isThinking, setIsThinking] = useState(false);
  
  // Deep Research toggle state
  const [useDeepResearch, setUseDeepResearch] = useState(false);
  
  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
    fetchIndividualFiles();
  }, []);
  
  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Nuevo efecto para hacer scroll cuando se carga una conversación
  useEffect(() => {
    // Cuando cambie la conversación actual o se terminen de cargar los mensajes
    if (!messagesLoading && messagesEndRef.current && messages.length > 0) {
      // Usa setTimeout para asegurar que el DOM se ha actualizado completamente
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [currentConversation, messagesLoading, messages.length]);
  
  // Fetch files when a subject is selected
  useEffect(() => {
    if (selectedSubject) {
      fetchSubjectFiles(selectedSubject.id);
    } else {
      setFiles([]);
    }
  }, [selectedSubject]);
  
  // Fetch messages when a chat subject is selected
  useEffect(() => {
    if (selectedChatSubject) {
      fetchSubjectMessages(selectedChatSubject.id);
    } else {
      setMessages([]);
    }
  }, [selectedChatSubject]);

  // Si estamos en modo chat y hay una conversación actual, configuramos el subject y cargamos los mensajes
  useEffect(() => {
    if (chatMode && currentConversation) {
      // Indicar que los mensajes están cargando
      setMessagesLoading(true);
      
      // Limpiar mensajes inmediatamente y explícitamente antes de cualquier otra operación
      setMessages([]);
      
      // Buscar el subject por ID si existe
      if (currentConversation.subjectId && currentConversation.subjectId !== "none") {
        const foundSubject = subjects.find(s => s.id === currentConversation.subjectId);
        if (foundSubject) {
          setSelectedChatSubject(foundSubject);
        } else {
          setSelectedChatSubject(null);
        }
      } else {
        setSelectedChatSubject(null);
      }
      
      // Cargar mensajes de la conversación actual
      if (currentConversation.id) {
        fetchConversationMessages(currentConversation.id);
      } else {
        // Si no hay ID de conversación, terminar la carga
        setMessagesLoading(false);
      }
    }
  }, [chatMode, currentConversation, subjects]);

  // API Handlers
  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await agtEduService.getSubjects();
      setSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Could not load subjects. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubjectFiles = async (subjectId) => {
    try {
      setLoading(true);
      const response = await agtEduService.getSubjectFiles(subjectId);
      setFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Could not load files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchIndividualFiles = async () => {
    try {
      setLoading(true);
      const response = await agtEduService.getIndividualFiles();
      console.log('Archivos individuales recibidos:', response.data);
      setIndividualFiles(response.data || []);
    } catch (error) {
      console.error('Error al obtener archivos individuales:', error);
      setError('Could not load individual files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubjectMessages = async (subjectId) => {
    try {
      setMessagesLoading(true);
      const response = await agtEduService.getSubjectMessages(subjectId);
      // Ordenar mensajes cronológicamente (más antiguos primero)
      const sortedMessages = (response.data || []).sort((a, b) => 
        new Date(a.createdAt) - new Date(b.createdAt)
      );
      setMessages(sortedMessages);
      
      // Hacer scroll al final después de que los mensajes se carguen
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching subject messages:', error);
      setError('Could not load messages. Please try again.');
    } finally {
      setMessagesLoading(false);
    }
  };
  
  const fetchConversationMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      
      // Limpiar mensajes antes de la petición para evitar mezcla
      setMessages([]);
      
      const response = await agtEduService.getConversationMessages(conversationId);
      
      // Asegurarse de que los mensajes estén ordenados cronológicamente (más antiguos primero)
      const sortedMessages = (response.data || []).sort((a, b) => 
        new Date(a.createdAt) - new Date(b.createdAt)
      );
      
      setMessages(sortedMessages);
      
      // Hacer scroll al final después de que los mensajes se carguen
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      setError('Could not load conversation messages. Please try again.');
    } finally {
      setMessagesLoading(false);
    }
  };
  
  const handleAddSubject = async () => {
    if (newSubject.trim()) {
      try {
        setLoading(true);
        const response = await agtEduService.createSubject({ name: newSubject.trim() });
        setSubjects((prev) => [...prev, response.data]);
        setNewSubject('');
        setShowSubjectInput(false);
      } catch (error) {
        console.error('Error adding subject:', error);
        setError('Could not create the subject. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleRemoveSubject = async (idToRemove) => {
    try {
      setLoading(true);
      await agtEduService.deleteSubject(idToRemove);
      setSubjects(subjects.filter((subject) => subject.id !== idToRemove));
      if (selectedChatSubject?.id === idToRemove) {
        setSelectedChatSubject(null);
      }
      if (selectedSubject?.id === idToRemove) {
        setSelectedSubject(null);
      }
    } catch (error) {
      console.error('Error removing subject:', error);
      setError('Could not delete the subject. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    setFilesCurrentPage(0); // Reset files pagination when changing subject
  };
  
  const toggleDeepResearch = () => {
    setUseDeepResearch((prev) => !prev);
  };
  
  const handleToggleIndividualFile = (file) => {
    setSelectedIndividualFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleOpenLibraryDialog = () => {
    // Actualizar la lista de archivos individuales antes de mostrar el diálogo
    fetchIndividualFiles();
    setShowIndividualFilesDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim()) {
      try {
        // Save message text before clearing
        let messageText = message.trim();
        
        // Prepare message data
        let messageData = {
          text: messageText,
          subjectId: selectedChatSubject?.id || null,
          conversationId: currentConversation?.id || null,
          useDeepResearch: useDeepResearch,
          fileIds: selectedIndividualFiles.map(file => file.id)
        };
        
        // IMPORTANT: Activate thinking indicator directly
        setIsThinking(true);
        const thinkingStartTime = Date.now();
        
        // Create user message object
        const userMessage = {
          id: 'temp-' + Date.now(),
          text: messageText,
          role: 'user',
          createdAt: new Date().toISOString(),
          metadata: {
            useDeepResearch,
            fileCount: selectedIndividualFiles.length,
            conversationId: currentConversation?.id || null
          },
          fileIds: selectedIndividualFiles.map(file => file.id)
        };
        
        // Create thinking message with explicit timestamp and metadata
        const thinkingMessage = {
          id: 'thinking-' + Date.now(),
          text: '',
          role: 'thinking',
          createdAt: new Date().toISOString(),
          metadata: {
            relatedToMessage: messageText,
            thinkingStartTime: thinkingStartTime // Store the start time in metadata
          }
        };
        
        // Clear message field IMMEDIATELY
        setMessage('');
        
        // Add both user message AND thinking message to interface IMMEDIATELY
        setMessages(prevMessages => [...prevMessages, userMessage, thinkingMessage]);
        
        // Scroll to bottom after adding user message
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
        }, 100);
        
        // Notify parent component about user message and thinking state
        if (onConversationUpdate) {
          const tempResponse = {
            success: true,
            data: {
              userMessage: userMessage,
              tempUserMessageOnly: true,
              thinkingStartTime: thinkingStartTime,
              thinkingMessage: thinkingMessage
            }
          };
          
          onConversationUpdate(tempResponse, selectedChatSubject);
        }
        
        // Send message to server
        const response = await agtEduService.sendMessage(messageData);
        
        // Calculate how much time has passed since the thinking indicator appeared
        const thinkingDuration = Date.now() - thinkingStartTime;
        
        // IMPORTANT: Ensure minimum display time for thinking indicator
        if (thinkingDuration < MIN_THINKING_DISPLAY_TIME) {
          await new Promise(resolve => setTimeout(resolve, MIN_THINKING_DISPLAY_TIME - thinkingDuration));
        }
        
        // Update messages with the assistant response by replacing the thinking message
        setMessages(prevMessages => {
          // Find the thinking message we just added
          const thinkingMsgIndex = prevMessages.findIndex(msg => 
            msg.id === thinkingMessage.id || 
            (msg.role === 'thinking' && msg.metadata?.relatedToMessage === messageText)
          );
          
          // Create a copy of messages to modify
          const updatedMessages = [...prevMessages];
          
          if (thinkingMsgIndex !== -1) {
            // Replace thinking message with assistant message
            updatedMessages[thinkingMsgIndex] = response.data.assistantMessage;
          } else {
            // If somehow thinking message is missing, add assistant message at end
            updatedMessages.push(response.data.assistantMessage);
          }
          
          // Update any temporary user messages with server data
          return updatedMessages.map(msg => {
            if ((msg.id === userMessage.id) || 
                (msg.id.startsWith('temp-') && msg.text === messageText)) {
              return {
                ...response.data.userMessage,
                id: response.data.userMessage.id || msg.id
              };
            }
            return msg;
          });
        });
        
        // Scroll to bottom after getting response
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
        }, 100);
        
        // Reset selected files after sending
        setSelectedIndividualFiles([]);
        
        // Notify parent component with complete response
        if (onConversationUpdate) {
          onConversationUpdate({
            ...response,
            data: {
              ...response.data,
              thinkingStartTime: thinkingStartTime // Pass thinking start time with response
            }
          }, selectedChatSubject);
        }
        
      } catch (error) {
        console.error('Error sending message:', error);
        
        setStatus({
          show: true,
          type: 'error',
          message: 'Error al enviar el mensaje'
        });
        
        setTimeout(() => {
          setStatus({ show: false, type: '', message: '' });
        }, 3000);
      } finally {
        // IMPORTANT: Turn off the thinking indicator AFTER everything is done
        setIsThinking(false);
      }
    }
  };
  
  const handleCloseError = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setError(null);
  };
  
  // Handle file upload completion
  const handleUploadComplete = (uploadedFiles) => {
    // Update file list for selected subject
    if (selectedSubject) {
      fetchSubjectFiles(selectedSubject.id);
    }
    
    // Also update subjects list to reflect new counts
    fetchSubjects();
  };
  
  // Handle individual file upload completion
  const handleIndividualUploadComplete = (uploadedFiles) => {
    // Update individual files list
    fetchIndividualFiles();
  };

  // Diferentes estilos según el modo de chat
  const containerPadding = chatMode 
    ? { px: 0, py: 0 } 
    : (isMobile ? { px: 3, py: 2 } : { p: 4 });
    
  const containerWidth = '100%';
  const containerMaxWidth = chatMode ? '100%' : (isMobile ? '100%' : '40rem');

  const colors = navigationColors(theme);

  return (
    <Box
      sx={{
        width: containerWidth,
        maxWidth: containerMaxWidth,
        mx: 'auto',
        ...containerPadding,
        color: colors.textDefault,
        backgroundColor: colors.background
      }}
    >
      {/* Si no estamos en modo chat, mostramos el título principal */}
      {!chatMode && (
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          sx={{ 
            mb: 3, 
            fontWeight: 600,
            textAlign: 'center',
            color: colors.textDefault
          }}
        >
          What assignment will AGT Edu help with today?
        </Typography>
      )}
      
      {/* Chat Section - solo mostrar en modo landing o si hay mensajes */}
      {(!chatMode && (messages.length > 0 || selectedChatSubject)) && (
        <Box sx={{ mb: 4 }}>
          {selectedChatSubject && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                p: 2,
                backgroundColor: colors.cardBackground,
                borderRadius: 2
              }}
            >
              <Folder size={20} style={{ color: colors.accent }} />
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  ml: 1, 
                  fontWeight: 500, 
                  color: colors.textDefault 
                }}
              >
                Subject: {selectedChatSubject.name}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Chip 
                label={`${selectedChatSubject.files?.length || 0} files`}
                size="small"
                sx={{ 
                  backgroundColor: colors.backgroundTertiary,
                  color: colors.textSecondary
                }}
              />
              <IconButton
                size="small"
                onClick={() => setSelectedChatSubject(null)}
                sx={{ ml: 1, color: colors.textSecondary }}
              >
                <X size={16} />
              </IconButton>
            </Box>
          )}
          
          <ScrollableMessagesContainer className="ChatMessagesContainer">
            {messagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {messages.map((msg) => (
                  msg.role === 'thinking' ? (
                    // Special rendering for thinking messages - ensure visibility
                    <Box 
                      key={msg.id} 
                      sx={{ 
                        width: '100%', 
                        mb: 2, 
                        opacity: 1, 
                        visibility: 'visible', 
                        display: 'flex', 
                        justifyContent: 'flex-start' 
                      }}
                    >
                      <ThinkingIcon 
                        colors={colors} 
                        startTime={msg.metadata?.thinkingStartTime || (msg.createdAt ? new Date(msg.createdAt).getTime() : Date.now())} 
                        key={`thinking-icon-${msg.id}`}
                      />
                    </Box>
                  ) : (
                    // Regular message rendering
                    <MessageContainer key={msg.id} role={msg.role}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 1,
                          ml: msg.role === 'user' ? 'auto' : 0,
                          mr: msg.role === 'assistant' ? 'auto' : 0
                        }}
                      >
                        {msg.role === 'assistant' && (
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              backgroundColor: colors.accent
                            }}
                          >
                            <Bot size={16} />
                          </Avatar>
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.textSecondary,
                            fontSize: '0.7rem'
                          }}
                        >
                          {msg.role === 'user' ? 'You' : 'AGT Edu'} • {formatDate(msg.createdAt)}
                          
                          {/* Show Deep Research indicator if it was used */}
                          {(msg.role === 'assistant' && msg.metadata?.deepResearch) && (
                            <span style={{ marginLeft: '6px', color: colors.accent }}>
                              • Deep Research
                            </span>
                          )}
                          
                          {/* Show attached files indicator if there are any */}
                          {(msg.fileIds && msg.fileIds.length > 0) && (
                            <span style={{ marginLeft: '6px', color: colors.textSecondary }}>
                              • {msg.fileIds.length} file(s)
                            </span>
                          )}
                        </Typography>
                        {msg.role === 'user' && (
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              ml: 1,
                              backgroundColor: colors.accent
                            }}
                          >
                            <User size={16} />
                          </Avatar>
                        )}
                      </Box>
                      {/* Show attached files if any */}
                      {(msg.fileIds && msg.fileIds.length > 0) && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.5,
                            mb: 1,
                            maxWidth: '80%',
                            ml: msg.role === 'assistant' ? 0 : 'auto',
                            mr: msg.role === 'user' ? 0 : 'auto'
                          }}
                        >
                          {msg.fileIds.map(fileId => {
                            const file = individualFiles.find(f => f.id === fileId);
                            return (
                              <Chip
                                key={fileId}
                                icon={<LibraryBig size={14} />}
                                label={file ? file.name : 'File'}
                                size="small"
                                sx={{
                                  backgroundColor: msg.role === 'user' ? `${colors.accent}80` : colors.backgroundTertiary,
                                  color: msg.role === 'user' ? '#ffffff' : colors.textSecondary,
                                  fontSize: '0.7rem',
                                  height: 24
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      <MessageBubble role={msg.role}>
                        {msg.role === 'assistant' ? (
                          <>
                            <MagicalMessageRenderer 
                              content={msg.text} 
                              isNew={msg.id === messages[messages.length - 1]?.id && msg.role === 'assistant'} 
                              theme={theme}
                              baseDelay={200}
                            />
                          </>
                        ) : (
                          <Typography>{msg.text}</Typography>
                        )}
                      </MessageBubble>
                    </MessageContainer>
                  )
                ))}
                
                {/* Fallback ThinkingIcon - only shown when isThinking is true but no thinking message exists */}
                {isThinking && !messages.some(msg => msg.role === 'thinking') && (
                  <Box 
                    sx={{ 
                      width: '100%', 
                      mb: 2, 
                      opacity: 1, 
                      visibility: 'visible',
                      display: 'flex', 
                      justifyContent: 'flex-start' 
                    }}
                  >
                    <ThinkingIcon colors={colors} startTime={Date.now()} />
                  </Box>
                )}
                
                <div ref={messagesEndRef} id="messages-end-ref" />
              </>
            )}
          </ScrollableMessagesContainer>
        </Box>
      )}
      
      {/* Formulario de envío de mensaje */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: 'relative',
          mb: chatMode ? 0 : 6
        }}
      >
        <Paper
          variant="elevation"
          elevation={0}
          sx={{
            position: 'relative',
            borderRadius: chatMode ? 6 : 6,
            overflow: 'hidden',
            backgroundColor: colors.cardBackground
          }}
        >
          {/* Top Bar */}
          <Box sx={{ position: 'relative', px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowSubjectSelector(!showSubjectSelector);
                }}
                startIcon={<Folder size={16} />}
                endIcon={<ChevronDown size={16} />}
                sx={{
                  height: 36,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: colors.borderColor,
                  color: colors.textSecondary,
                  borderRadius: 999,
                  '&:hover': {
                    backgroundColor: colors.cardBackground
                  }
                }}
              >
                {isMobile ? (
                  <Box sx={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedChatSubject ? selectedChatSubject.name : 'Select'}
                  </Box>
                ) : (
                  selectedChatSubject ? selectedChatSubject.name : 'Select a subject'
                )}
              </Button>

              {showSubjectSelector && (
                <SubjectSelectorMenu>
                  {subjects.length > 0 ? (
                    subjects.map((subject) => (
                      <Button
                        key={subject.id}
                        onClick={() => {
                          setSelectedChatSubject(subject);
                          setShowSubjectSelector(false);
                        }}
                        startIcon={<Folder size={16} />}
                        sx={{
                          justifyContent: 'flex-start',
                          color: colors.textDefault,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          width: '100%',
                          borderRadius: 0,
                          '&:hover': {
                            backgroundColor: colors.cardBackground
                          }
                        }}
                      >
                        {subject.name}
                      </Button>
                    ))
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      No subjects available
                      </Typography>
                      <Button
                        variant="text"
                        sx={{ mt: 1, color: colors.accent }}
                        onClick={() => {
                          setShowSubjectSelector(false);
                          setShowSubjectInput(true);
                        }}
                      >
                        Create subject
                      </Button>
                    </Box>
                  )}
                </SubjectSelectorMenu>
              )}
            </Box>
            
            {/* Selected subject label (desktop) */}
            {selectedChatSubject && !isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  ml: 2,
                  backgroundColor: colors.accent,
                  color: '#FFFFFF',
                  borderRadius: 999,
                  fontSize: '0.875rem'
                }}
              >
                <Folder size={14} />
                {selectedChatSubject.name}
                <IconButton
                  size="small"
                  onClick={() => setSelectedChatSubject(null)}
                  sx={{
                    color: '#FFFFFF',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.8)'
                    }
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Middle Bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              px: 2,
              py: 2
            }}
          >
            {/* Show selected individual files */}
            {selectedIndividualFiles.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                width: '100%',
                mb: 2
              }}>                
                {/* Files chips */}
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1,
                  width: '100%'
                }}>
                  {selectedIndividualFiles.map(file => (
                    <Chip
                      key={file.id}
                      label={file.name}
                      onDelete={() => {
                        handleToggleIndividualFile(file);
                      }}
                      icon={<LibraryBig size={14} />}
                      sx={{
                        backgroundColor: `${colors.accent}20`,
                        color: colors.textDefault,
                        borderRadius: 999,
                        '& .MuiChip-deleteIcon': {
                          color: colors.textSecondary,
                          '&:hover': {
                            color: colors.error
                          }
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                variant="outlined"
                placeholder={chatMode ? "Escribe un mensaje..." : "Ask AGT Edu a question..."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
                multiline={chatMode}
                maxRows={chatMode ? 5 : 1}
                InputProps={{
                  sx: {
                    backgroundColor: colors.inputBackground,
                    color: colors.textDefault,
                    '& fieldset': {
                      borderColor: 'transparent !important',
                      borderWidth: '1px !important',
                      transition: 'none !important'
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent !important',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent !important',
                      borderWidth: '1px !important'
                    },
                    '& input::placeholder': {
                      color: colors.textSecondary,
                      opacity: 1
                    },
                    '& input': {
                      color: colors.textDefault
                    }
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: colors.inputBackground
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: colors.textDefault
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent'
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                    borderWidth: '1px'
                  }
                }}
              />
              <IconButton
                type="submit"
                disabled={loading || !message.trim()}
                sx={{
                  ml: 2,
                  color: '#FFFFFF',
                  backgroundColor: colors.accent,
                  '&:hover': {
                    backgroundColor: colors.accent
                  },
                  '&.Mui-disabled': {
                    backgroundColor: `${colors.accent}50`,
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <ArrowUp size={20} />}
              </IconButton>
            </Box>
          </Box>

          {/* Bottom Bar with Deep Research */}
          <Box sx={{ px: 2, py: 2 }}>
            {isMobile ? (
              // Mobile version with abbreviated buttons
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', overflowX: 'auto' }}>
                <IndividualFileUploader 
                  colors={colors}
                  onUploadComplete={handleIndividualUploadComplete}
                />
                
                <Button
                  variant="outlined"
                  onClick={toggleDeepResearch}
                  sx={{
                    height: 36,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    px: 1.5,
                    color: useDeepResearch ? '#FFFFFF' : colors.textSecondary,
                    backgroundColor: useDeepResearch ? `${colors.accent}80` : 'transparent',
                    borderColor: useDeepResearch ? colors.accent : colors.borderColor,
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: useDeepResearch ? `${colors.accent}90` : colors.cardBackground,
                      borderColor: useDeepResearch ? colors.accent : colors.borderColor
                    },
                    flexShrink: 0
                  }}
                >
                  Deep Research
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleOpenLibraryDialog}
                  startIcon={<LibraryBig size={16} color={selectedIndividualFiles.length > 0 ? "#FFFFFF" : colors.textSecondary} />}
                  endIcon={selectedIndividualFiles.length > 0 ? <Chip 
                    label={selectedIndividualFiles.length} 
                    size="small"
                    sx={{ 
                      height: 20,
                      minWidth: 20,
                      backgroundColor: '#FFFFFF',
                      color: colors.accent
                    }}
                  /> : null}
                  sx={{
                    height: 36,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    px: 1.5,
                    color: selectedIndividualFiles.length > 0 ? '#FFFFFF' : colors.textSecondary,
                    backgroundColor: selectedIndividualFiles.length > 0 ? `${colors.accent}80` : 'transparent',
                    borderColor: selectedIndividualFiles.length > 0 ? colors.accent : colors.borderColor,
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: selectedIndividualFiles.length > 0 ? `${colors.accent}90` : colors.cardBackground,
                      borderColor: selectedIndividualFiles.length > 0 ? colors.accent : colors.borderColor
                    },
                    flexShrink: 0
                  }}
                >
                  Files
                </Button>
              </Box>
            ) : (
              // Desktop version with horizontal buttons
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', overflowX: 'auto' }}>
                <IndividualFileUploader 
                  colors={colors}
                  onUploadComplete={handleIndividualUploadComplete}
                />
                
                <Button
                  variant="outlined"
                  onClick={toggleDeepResearch}
                  startIcon={<Microscope size={16} color={useDeepResearch ? "#FFFFFF" : colors.textSecondary} />}
                  sx={{
                    height: 36,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: useDeepResearch ? '#FFFFFF' : colors.textSecondary,
                    backgroundColor: useDeepResearch ? `${colors.accent}80` : 'transparent',
                    borderColor: useDeepResearch ? colors.accent : colors.borderColor,
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: useDeepResearch ? `${colors.accent}90` : colors.cardBackground,
                      borderColor: useDeepResearch ? colors.accent : colors.borderColor
                    }
                  }}
                >
                  Deep Research
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleOpenLibraryDialog}
                  startIcon={<LibraryBig size={16} color={selectedIndividualFiles.length > 0 ? "#FFFFFF" : colors.textSecondary} />}
                  endIcon={selectedIndividualFiles.length > 0 ? <Chip 
                    label={selectedIndividualFiles.length} 
                    size="small"
                    sx={{ 
                      height: 20,
                      minWidth: 20,
                      backgroundColor: '#FFFFFF',
                      color: colors.accent
                    }}
                  /> : null}
                  sx={{
                    height: 36,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: selectedIndividualFiles.length > 0 ? '#FFFFFF' : colors.textSecondary,
                    backgroundColor: selectedIndividualFiles.length > 0 ? `${colors.accent}80` : 'transparent',
                    borderColor: selectedIndividualFiles.length > 0 ? colors.accent : colors.borderColor,
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: selectedIndividualFiles.length > 0 ? `${colors.accent}90` : colors.cardBackground,
                      borderColor: selectedIndividualFiles.length > 0 ? colors.accent : colors.borderColor
                    }
                  }}
                >
                  {isMobile ? "Library|" : "Library"}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Processing status */}
      {status.show && (
        <Alert 
          severity={status.type} 
          sx={{ 
            mb: 3, 
            borderRadius: 2 
          }}
        >
          {status.message}
        </Alert>
      )}

      {/* Subjects and Files Section - Solo mostrar en modo landing y cuando no hay modo chat */}
      {!chatMode && (
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 1.5 : 2,
            borderRadius: 2,
            backgroundColor: colors.background
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          )}

          {!loading && selectedSubject ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: isMobile ? 1 : 0
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowLeft size={20} />}
                  onClick={() => setSelectedSubject(null)}
                  sx={{
                    color: colors.textSecondary,
                    textTransform: 'none',
                    '&:hover': {
                      color: colors.textDefault
                    },
                    padding: isMobile ? 0 : 'auto',
                    minWidth: isMobile ? 'auto' : '64px'
                  }}
                >
                  <Typography 
                    variant={isMobile ? "body1" : "h6"} 
                    sx={{ 
                      ml: 1, 
                      fontWeight: 500, 
                      color: colors.textDefault,
                      fontSize: isMobile ? '1rem' : 'inherit'
                    }}
                  >
                    {selectedSubject.name}
                  </Typography>
                </Button>
                
                {/* Replace upload button with FileUploader */}
                <FileUploader 
                  subjectId={selectedSubject.id} 
                  onUploadComplete={handleUploadComplete}
                  colors={colors}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {files.length > 0 ? (
                  files
                    .slice(
                      filesCurrentPage * filesPerPage,
                      filesCurrentPage * filesPerPage + filesPerPage
                    )
                    .map((file) => (
                      <Box
                        key={file.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: isMobile ? 1.5 : 2,
                          backgroundColor: colors.cardBackground,
                          borderRadius: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: colors.borderColor
                          }
                        }}
                      >
                        <LibraryBig size={20} style={{ color: colors.textSecondary }} />
                        <Typography 
                          sx={{ 
                            flex: 1, 
                            color: colors.textDefault,
                            fontSize: isMobile ? '0.875rem' : 'inherit',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Delete file
                            agtEduService.deleteFile(file.id)
                              .then(() => {
                                setFiles(files.filter(f => f.id !== file.id));
                                // Also update subjects list to reflect the change in count
                                fetchSubjects();
                              })
                              .catch(err => {
                                console.error('Error deleting file:', err);
                                setError('Could not delete the file');
                              });
                          }}
                          sx={{
                            color: colors.textSecondary,
                            '&:hover': {
                              color: colors.error
                            }
                          }}
                        >
                          <X size={16} />
                        </IconButton>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', color: colors.textSecondary, py: 4 }}
                  >
                    No files in this subject
                  </Typography>
                )}
                  
                {/* Files pagination */}
                {files.length > filesPerPage && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: 2,
                    gap: 1
                  }}>
                    <Button
                      variant="outlined"
                      disabled={filesCurrentPage === 0}
                      onClick={() => setFilesCurrentPage(prev => Math.max(0, prev - 1))}
                      sx={{
                        minWidth: '36px',
                        width: '36px',
                        height: '36px',
                        p: 0,
                        borderRadius: '50%',
                        borderColor: colors.borderColor,
                        color: filesCurrentPage === 0 ? colors.borderColor : colors.textSecondary,
                        '&:hover': {
                          backgroundColor: colors.cardBackground,
                          borderColor: colors.borderColor
                        }
                      }}
                    >
                      {'<'}
                    </Button>
                    
                    <Typography sx={{ 
                      color: colors.textSecondary,
                      mx: 1,
                      fontSize: '0.875rem'
                    }}>
                      {filesCurrentPage + 1} / {Math.ceil(files.length / filesPerPage)}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      disabled={filesCurrentPage >= Math.ceil(files.length / filesPerPage) - 1}
                      onClick={() => setFilesCurrentPage(prev => prev + 1)}
                      sx={{
                        minWidth: '36px',
                        width: '36px',
                        height: '36px',
                        p: 0,
                        borderRadius: '50%',
                        borderColor: colors.borderColor,
                        color: filesCurrentPage >= Math.ceil(files.length / filesPerPage) - 1 
                          ? colors.borderColor 
                          : colors.textSecondary,
                        '&:hover': {
                          backgroundColor: colors.cardBackground,
                          borderColor: colors.borderColor
                        }
                      }}
                    >
                      {'>'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            !loading && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: isMobile ? 1 : 0
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Folder size={20} style={{ color: colors.textSecondary }} />
                    <Typography 
                      variant={isMobile ? "body1" : "h6"} 
                      sx={{ 
                        fontWeight: 500, 
                        color: colors.textDefault,
                        fontSize: isMobile ? '1rem' : 'inherit'
                      }}
                    >
                      My Subjects
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => setShowSubjectInput(true)}
                    startIcon={<Plus size={16} color="white" />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      backgroundColor: colors.accent,
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: colors.accent
                      },
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      px: isMobile ? 2 : 3
                    }}
                  >
                    {isMobile ? "Add" : "Add Subject"}
                  </Button>
                </Box>

                {showSubjectInput && (
                  <Box sx={{ 
                    display: 'flex', 
                    gap: isMobile ? 1 : 2, 
                    mb: 2,
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <TextField
                      variant="outlined"
                      placeholder="Subject name"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 999,
                          borderColor: colors.borderColor,
                          color: colors.textDefault,
                          '& fieldset': {
                            borderColor: colors.borderColor
                          },
                          '&:hover fieldset': {
                            borderColor: colors.borderColor
                          }
                        }
                      }}
                    />
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1,
                      mt: isMobile ? 1 : 0
                    }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleAddSubject}
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          backgroundColor: colors.success,
                          '&:hover': {
                            backgroundColor: colors.success
                          },
                          flex: isMobile ? 1 : 'inherit'
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowSubjectInput(false)}
                        sx={{
                          color: colors.textSecondary,
                          borderColor: colors.borderColor,
                          borderRadius: 999,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: colors.cardBackground
                          },
                          flex: isMobile ? 1 : 'inherit'
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Subjects with pagination */}
                <Box>
                  {subjects.length > 0 ? (
                    <Grid container spacing={isMobile ? 1 : 2}>
                      {subjects
                        .slice(
                          currentPage * subjectsPerPage,
                          currentPage * subjectsPerPage + subjectsPerPage
                        )
                        .map((subject) => (
                          <Grid item xs={12} sm={6} md={4} key={subject.id}>
                            <Box
                              onClick={() => handleSubjectClick(subject)}
                              sx={{
                                position: 'relative',
                                cursor: 'pointer'
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  px: isMobile ? 2 : 3,
                                  py: 2,
                                  backgroundColor: colors.cardBackground,
                                  borderRadius: 2,
                                  '&:hover': {
                                    backgroundColor: colors.borderColor
                                  }
                                }}
                              >
                                <Folder size={20} style={{ color: colors.textSecondary }} />
                                <Typography sx={{ 
                                  flex: 1, 
                                  color: colors.textDefault,
                                  fontSize: isMobile ? '0.875rem' : 'inherit',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {subject.name}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveSubject(subject.id);
                                  }}
                                  sx={{
                                    color: colors.textSecondary,
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                      color: colors.error
                                    },
                                    '.MuiGrid-root:hover &': {
                                      opacity: 1
                                    }
                                  }}
                                >
                                  <X size={16} />
                                </IconButton>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 16,
                                  color: colors.textSecondary
                                }}
                              >
                                {/* Use a ternary operator to handle cases where files may be undefined or null */}
                                {Array.isArray(subject.files) ? subject.files.length : 0} {isMobile ? 'files' : 'files'}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                        </Grid>
                  ) : (
                    !showSubjectInput && (
                      <Typography
                        variant="body2"
                        sx={{ textAlign: 'center', color: colors.textSecondary, py: 4 }}
                      >
                        You haven't added any subjects yet
                      </Typography>
                    )
                  )}
                  
                  {/* Pagination controls */}
                  {subjects.length > subjectsPerPage && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      mt: 3,
                      gap: 1
                    }}>
                      <Button
                        variant="outlined"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        sx={{
                          minWidth: '36px',
                          width: '36px',
                          height: '36px',
                          p: 0,
                          borderRadius: '50%',
                          borderColor: colors.borderColor,
                          color: currentPage === 0 ? colors.borderColor : colors.textSecondary,
                          '&:hover': {
                            backgroundColor: colors.cardBackground,
                            borderColor: colors.borderColor
                          }
                        }}
                      >
                        {'<'}
                      </Button>
                      
                      <Typography sx={{ 
                        color: colors.textSecondary,
                        mx: 1,
                        fontSize: '0.875rem'
                      }}>
                        {currentPage + 1} / {Math.ceil(subjects.length / subjectsPerPage)}
                      </Typography>
                      
                      <Button
                        variant="outlined"
                        disabled={currentPage >= Math.ceil(subjects.length / subjectsPerPage) - 1}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        sx={{
                          minWidth: '36px',
                          width: '36px',
                          height: '36px',
                          p: 0,
                          borderRadius: '50%',
                          borderColor: colors.borderColor,
                          color: currentPage >= Math.ceil(subjects.length / subjectsPerPage) - 1 
                            ? colors.borderColor 
                            : colors.textSecondary,
                          '&:hover': {
                            backgroundColor: colors.cardBackground,
                            borderColor: colors.borderColor
                          }
                        }}
                      >
                        {'>'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            )
          )}
        </Paper>
      )}

      {/* Dialog for selecting individual files */}
      <Dialog
        open={showIndividualFilesDialog}
        onClose={() => setShowIndividualFilesDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: colors.background
          }
        }}
      >
        <DialogTitle sx={{ color: colors.textDefault }}>
          Select files for analysis
        </DialogTitle>
        
        <DialogContent>
          {individualFiles.length > 0 ? (
            <List sx={{ bgcolor: colors.cardBackground, borderRadius: 1 }}>
            {individualFiles.map((file) => {
              const isSelected = selectedIndividualFiles.some(f => f.id === file.id);
              
              return (
                <ListItem 
                  key={file.id}
                  button
                  onClick={() => {
                    handleToggleIndividualFile(file);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    backgroundColor: isSelected 
                      ? `${colors.accent}20` 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected 
                        ? `${colors.accent}30` 
                        : colors.cardBackground
                    }
                  }}
                >
                  <ListItemIcon>
                    <LibraryBig size={20} style={{ color: colors.accent }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={file.name} 
                    primaryTypographyProps={{ color: colors.textDefault }}
                  />
                  {isSelected && (
                    <Chip 
                      label="Selected" 
                      size="small"
                      sx={{ 
                        backgroundColor: colors.accent,
                        color: '#ffffff'
                      }}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.textSecondary, py: 4 }}
          >
            No individual files available
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={() => setShowIndividualFilesDialog(false)}
          variant="contained"
          sx={{
            backgroundColor: colors.accent,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: colors.accent
            }
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>

    {/* Error notification */}
    <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
      <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
        {error}
      </Alert>
    </Snackbar>
  </Box>
);
};

export default ChatInputMUI;